import * as Constants from "../constants";
import {
  ADV_SEARCH_DATES,
  ADV_SEARCH_DROPDOWNS,
  TAdvConstraint,
  TAdvSearchDate,
  TConstraint,
  TQueryItem
} from "../constants";
import Utils from "./index";

import {ISearchDate, ISearchState} from "../types/redux/search";

/* Data */
import FILTERS_JSON from "../assets/data/filters.json";
import {IFilterDB, TFilter} from "../types/search";

/* @ts-ignore */
const FILTERS_DB: IFilterDB = FILTERS_JSON as IFilterDB;

const SearchUtils = {
  convertQueryParams: (params: string): TQueryItem[] => {
    let escapeInsideQuotes = params.replace(/"([^"]*)"/g, (match) => {
      // Remove the quotes first
      const inner = match.slice(1, -1);
      // Replace semicolons and commas inside the quotes
      const escaped = inner
        .replace(/;/g, '&#x3B')
        .replace(/,/g, '%2C');
      // Re-wrap with quotes
      return `"${escaped}"`;
    });

    const queryStrings = escapeInsideQuotes.split(';');
    const queries: TQueryItem[] = [];

    for (const queryString of queryStrings) {
      const [operator, field, match, rawTerm] = queryString.split(',');

      if (!operator || !field || !match || rawTerm === undefined) {
        continue; // skip invalid entries
      }

      let term = SearchUtils.unEscapeInsideQuotes(rawTerm);

      queries.push({
        operator,
        field,
        match,
        term,
      });
    }

    return queries;
  },

  convertDateParams: (param: string): ISearchDate[] =>{
    if (!param) return [];

    const dates: ISearchDate[] = [];
    const datesArr: string[] = param.split(';');
    for (let i=0; i<datesArr.length; i++) {
      if (datesArr[i].length > 0) {
        const date: string[] = datesArr[i].split(',');
        dates.push({
          id:date[0],
          field: date[1],
          from: date[2],
          to: date[3]
        })
      }
    }
    return dates
  },

  sortDefault: (params: string): string => {
    if (window.location.pathname === Constants.MY_LIBRARY_PATHNAME) {
      return Constants.MY_LIBRARY_DEFAULT_SORT
    } else {
      const searchParams: URLSearchParams = new URLSearchParams(params);
      const sortParam: string | null = searchParams.get(Constants.SORT_KEY);
      if (sortParam) {
        return sortParam
      } else if (searchParams.get(Constants.DB_SET_KEY) === Constants.BIBLIOGRAPHY) {
        /* todo: no setting for this but there is for document */
        return Constants.DEFAULT_BIBLIOGRAPHY_SORT;
      } else {
        const savedSettings: string | null = localStorage.getItem(Constants.SETTINGS_KEY);
        if (savedSettings) {
          return JSON.parse(savedSettings).sort;
        }
      }
    }
    return Constants.DEFAULT_DOCUMENT_SORT;
  },

  resultsPerPageDefault: (params: string): number => {
    if (window.location.pathname === Constants.MY_LIBRARY_PATHNAME) {
      return Constants.DEFAULT_RESULT_PER_PAGE
    } else {
      const searchParams: URLSearchParams = new URLSearchParams(params);
      const nppParam: string | undefined = searchParams.get(Constants.RESULTS_PER_PAGE_KEY);
      if (nppParam) {
        let npp: number | undefined = parseInt(nppParam);
        if (isNaN(npp)) {
          return Constants.DEFAULT_RESULT_PER_PAGE;
        } else {
          return parseInt(nppParam);
        }
      } else {
        const savedSettings: string | null = localStorage.getItem(Constants.SETTINGS_KEY);
        if (savedSettings) {
          let rrp: number;
          try {
            rrp = JSON.parse(savedSettings).results_per_page;
          } catch (e) {}

          if (isNaN(rrp)) {
            return Constants.DEFAULT_RESULT_PER_PAGE
          } else {
            return rrp;
          }
        }
      }
    }
  },

  urlHasAppliedFilters: (params: string): boolean => {
    const searchParams: URLSearchParams = new URLSearchParams(params);
    const filtersValue: string = searchParams.get(Constants.FILTERS_KEY);
    if (filtersValue){
      const filtersArr: string[] = filtersValue.split(',');
      return filtersArr.length > 0;
    }
    return true;
  },

  industry: (): string => {
    const {industry } = Utils.parseIndustrySection();
    return ((industry === Constants.GLOBAL) ||
      (industry === Constants.INDUSTRIES) ||
      (industry === '') ||
      (industry === Constants.HOME) ||
      (industry === Constants.MY_LIBRARY)
    ) ? Constants.ALL_INDUSTRIES : industry
  },

  fromDataSource: (params: string): ISearchState => {
    const searchParams: URLSearchParams = new URLSearchParams(params);

    const filterKey = (): string =>{
      const dbParamValue: string = searchParams.get(Constants.DB_SET_KEY);
      if (dbParamValue) {
        return dbParamValue;
      } if (window.location.pathname === Constants.MY_LIBRARY_PATHNAME) {
        return Constants.TAGS
      } else{
        /* @ts-ignore*/
        let key: string = null;
        const urlSegments: string[] = window.location.pathname.split('/');
        if ((urlSegments[2] === Constants.BIBLIOGRAPHY) || (urlSegments[2] === Constants.DOCUMENTS)) {
          key = urlSegments[2]
        }
        return key
      }
    }

    const dbSetValue = (): string | null =>{
      const dbParamValue: string = searchParams.get(Constants.DB_SET_KEY);
      if (dbParamValue) {
        return dbParamValue;
      } if (window.location.pathname === Constants.MY_LIBRARY_PATHNAME) {
        return Constants.TAGS;
      } else {
        let key: string | null = null;
        const urlSegments: string[] = window.location.pathname.split('/');
        if ((urlSegments[2] === Constants.BIBLIOGRAPHY) || (urlSegments[2] === Constants.DOCUMENTS)) {
          key = urlSegments[2]
        }
        return key;
      }
    }

    const defaultFiltersDB = (): IFilterDB=> {
      const filtersClone: IFilterDB = JSON.parse(JSON.stringify(FILTERS_DB));
      filtersClone.documents = filtersClone.documents.filter((f: TFilter) => (f.id !== Constants.DISPLAY))
      return filtersClone
    };

    const displayFilter = (): TFilter => {
      const filtersClone: IFilterDB = JSON.parse(JSON.stringify(FILTERS_DB));
      const _displayFilter: TFilter = filtersClone.documents.filter((f: TFilter) => f.id === Constants.DISPLAY)[0];
      return _displayFilter;
    };

    const resultsPerPage = ():number => {
      const paramValue: number = SearchUtils.resultsPerPageDefault(params);
      if (isNaN(paramValue)) {
        return Constants.DEFAULT_RESULT_PER_PAGE
      } else {
        return paramValue
      }
    }

    return {
      /* @ts-ignore */
      industry: searchParams.get(Constants.INDUSTRY) ? searchParams.get(Constants.INDUSTRY) : SearchUtils.industry(),
      db_set: dbSetValue(),
      /* @ts-ignore */ /* query: TQueryItem[];*/
      query: searchParams.get(Constants.QUERY_KEY) ? SearchUtils.convertQueryParams(searchParams.get(Constants.QUERY_KEY).toString()) : [Constants.DEFAULT_SEARCH],
      /* @ts-ignore */
      dates: searchParams.get(Constants.DATES_KEY) ? SearchUtils.convertDateParams(searchParams.get(Constants.DATES_KEY)) : [],
      /* @ts-ignore */
      results_per_page: resultsPerPage(),
      /* @ts-ignore */
      current_page: searchParams.get(Constants.PAGE_KEY) ? parseInt(searchParams.get(Constants.PAGE_KEY)) : 1,
      sort: SearchUtils.sortDefault(params),
      /* @ts-ignore */
      filters_db: defaultFiltersDB(),
      filtersLoaded: false,
      available_filter_options: [],
      /* @ts-ignore */
      filter_display: displayFilter(),
      /* @ts-ignore */
      filters_key: filterKey(),
      selected_filters_ids: searchParams.get(Constants.FILTERS_KEY) ? searchParams.get(Constants.FILTERS_KEY).split(',') : [],
      applied_filters: [],
      recent_query: false,
      suspendSearch: SearchUtils.urlHasAppliedFilters(params),
      initialSearch: true
    };
  },

  searchParamsToConstraints: (searchString: string | null):TAdvConstraint[] => {
    const queries: TAdvConstraint[] = [];
    if (searchString) {
      let escapedString = SearchUtils.escapeInsideQuotes(searchString);
      const queryString: string[] = escapedString.split(';');

      /* if default search then replace *:* with empty string */
      if (queryString.length === 1) {
        const qsValues: any[] = queryString[0].split(',');
        if (SearchUtils.isDefaultQuery([{
          operator: qsValues[0],
          field: qsValues[1],
          match: qsValues[2],
          term: qsValues[3]
        }])) {
          return [{...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT}]
        }
      }

      /* otherwise parse the query */
      queryString.forEach((qs)=>{
        const values: any[] = qs.split(',');
        if (values.length === 4) { /* todo: short term fix for search on ; then click advanced search */
          const constraint: TAdvConstraint = {...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT};

          const operator: string | null = (!values[0] )|| (values[0] === 'null') ? null : values[0];
          constraint.operator = {
            value: operator,
            label: operator ? ADV_SEARCH_DROPDOWNS.operator.find((item:TConstraint) => {
              return (item.value === operator)
            }).label : operator
          };

          constraint.field = {
            value: values[1],
            description: null,
            label: ADV_SEARCH_DROPDOWNS.field.find((item:TConstraint) =>
                (item.value === values[1])).label
          };

          constraint.match = {
            value: values[2],
            label: ADV_SEARCH_DROPDOWNS.match.find((item:TConstraint) =>
                (item.value === values[2])).label
          };

          constraint.term = SearchUtils.unEscapeInsideQuotes((values[3] === 'null' ? '' : values[3]));

          queries.push(constraint)
        }
      })
    } else {
      queries.push({...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT})
    }
    return queries
  },

  dateParamToDates: (datesParams: string | null):TAdvSearchDate[] => {
    const dates: TAdvSearchDate[] = [...ADV_SEARCH_DATES];
    if (datesParams) {
      const datesString: string[] = datesParams.split(';');
      datesString.forEach((ds)=>{
        const date:string[] = ds.split(',');
        const dateEntry: TAdvSearchDate | undefined = dates.find(d=>d.field === date[1]);
        if (dateEntry) {
          dateEntry.start = new Date(date[2]).toLocaleDateString();
          dateEntry.end = new Date(date[3]).toLocaleDateString();
        }
      })
    }
    return dates
  },

  searchQueryTerms: (searchQuery: TQueryItem[]): string | null => {
    let terms: string | null = null;
     searchQuery.forEach((queryItem: TQueryItem) => {
       if ((queryItem.operator === null) || (queryItem.operator === Constants.CONTAINS)) {
         terms = (terms === null) ? queryItem.term : (terms + queryItem.term)
       }
    })
    return terms;
  },

  searchQueryToString: (searchQuery: TQueryItem[]):string => {
    return searchQuery.map((queryItem: TQueryItem) => {
      return queryItem.operator + ',' + queryItem.field + ',' + queryItem.match + ',' + queryItem.term;
    }).join(';')
  },

  searchQueryToUrlSearchParams: (searchState: ISearchState): URLSearchParams =>{
    const urlSearchParams:URLSearchParams =  new URLSearchParams();
    urlSearchParams.set(Constants.QUERY_KEY, SearchUtils.searchQueryToString(searchState.query))
    urlSearchParams.set(Constants.DB_SET_KEY, searchState.db_set)
    urlSearchParams.set(Constants.INDUSTRY_KEY, searchState.industry)
    urlSearchParams.set(Constants.SORT_KEY, searchState.sort)
    urlSearchParams.set(Constants.PAGE_KEY, String(searchState.current_page))
    urlSearchParams.set(Constants.RESULTS_PER_PAGE_KEY, String(searchState.results_per_page))

    if (searchState.selected_filters_ids.length > 0) {
      urlSearchParams.set(Constants.FILTERS_KEY, searchState.selected_filters_ids.join(','))
    }

    if (searchState.dates.length > 0) {
      let dateString: string = '';
      searchState.dates.forEach(d=>dateString +=`${d.id},${d.field},${d.from},${d.to};`)
      urlSearchParams.set(Constants.DATES_KEY, dateString)
    }

    return urlSearchParams;
  },

  //http://localhost:3000/results/#q=eggplant&h={"hideDuplicates":false,"hideFolders":true}&cache=true&count=6465
  transposeLegacyParams: (params: string): URLSearchParams => {
    const searchParams:URLSearchParams = new URLSearchParams(params);
    const query: string = searchParams.get(Constants.QUERY_KEY);
    const hidden: any = JSON.parse(searchParams.get('h'));
    let facets: any = searchParams.get('fq');

    /* Query */
    const transposedParams:URLSearchParams = new URLSearchParams();
    const transposedQuery:string = 'null,all,contains,' + query;
    transposedParams.set(Constants.QUERY_KEY,transposedQuery)

    /* Filters  */
    let transposedFilters: string = '';
    if (hidden) {
      transposedFilters += hidden["hideDuplicates"] ? Constants.DUPLICATES + ',' : '';
      transposedFilters += hidden["hideRestricted"] ? Constants.RESTRICTED + ',' : '';
      transposedFilters += hidden["hideFolders"] ? Constants.FOLDERS + ',' : '';
    }

    /* Facet Filters */
    if (!Array.isArray(facets)) {
      facets = JSON.parse(facets);
    }

    const transposedIndustry: string = transposedParams.get('subsite');
    if (transposedIndustry) {
      transposedParams.set(Constants.INDUSTRY, transposedIndustry);
    }
    //fq=[["daterange_facet","-631152000","decade","1950s"]]
    //dt=dddate_1950-01-01t00:00:00z,dddate,12/31/1949,12/31/1950;
    let transposedDate: string = '';
    facets?.forEach((f:string[])=> {
      if (f[0] === 'daterange_facet') {
        const decade: string = f[3].substring(0, f[3].length-1);
        const date: Date = new Date('12/31/' + decade)
         transposedDate += 'dddate_' + decade +'-01-01t00:00:00z,dddate,12/31/' + (date.getFullYear()-1) + ',12/31/'+ decade + ';';
      } else {
        transposedFilters += (f[0] + '_' + f[1].replaceAll(' ','-') + ',');
      }
    });

    if (transposedDate) {
      transposedParams.set(Constants.DATES_KEY, transposedDate);
    }

    if (transposedFilters.length > 0) {
      transposedFilters = transposedFilters.substring(0, transposedFilters.length - 1); // remove trailing comma
      transposedParams.set(Constants.FILTERS_KEY, transposedFilters);
    }

    return transposedParams
  },

  isDefaultQuery: (searchQuery: TQueryItem[]):boolean => {
    /* todo: jain - this include date, filters or create a constant to denote default */
    return (searchQuery.length === 1) &&
      (String(Constants.DEFAULT_SEARCH.operator) === String(searchQuery[0].operator)) &&
      (Constants.DEFAULT_SEARCH.field === searchQuery[0].field) &&
      (Constants.DEFAULT_SEARCH.match === searchQuery[0].match) &&
      (Constants.DEFAULT_SEARCH.term === searchQuery[0].term)
  },

  /* converts array of TQueryItem to search string for search input field */
  convertSearchQuery: (searchQuery: TQueryItem[]):string => {
    let q: string = '';
    for (let i=0; i <searchQuery.length; i++) {
      const queryItem: TQueryItem = searchQuery[i];
      if (queryItem.term && (queryItem.term.length > 0)) {
        let constraint: string = '';
        const hasField: boolean = (queryItem.field !== Constants.ALL);
        if (hasField) {
          constraint += queryItem.field + ':';
        }

        let term:string;
        if (queryItem.match === Constants.CONTAINS_EXACT) {
          term = '"' +  queryItem.term + '"';
        } else {
          term = queryItem.term;
        }

        constraint += term;

        if (i > 0) { /* ignore initial operator */
          constraint = ' ' + queryItem.operator + ' ' + constraint;
        }
        if (searchQuery.length > 1) {
          q += constraint;
        } else {
          q = constraint;
        }
      }
    }
    return q;
  },

  unEscapeInsideQuotes: (input: string): string => {
    return input.replace(/&#x3B/g, ';')
      .replace(/%2C/g, ',');
  },

  escapeInsideQuotes: (input: string): string => {
    return input.replace(/"([^"]*)"/g, (match) => {
      // Remove the quotes first
      const inner = match.slice(1, -1);
      // Replace semicolons and commas inside the quotes
      const escaped = inner
        .replace(/;/g, '&#x3B')
        .replace(/,/g, '%2C');
      // Re-wrap with quotes
      return `"${escaped}"`;
    });
  }
};

export default SearchUtils;
